import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, Cookie } from "lucide-react";

// ── Region detection ───────────────────────────────────────────────────────
// We flag EEA / UK visitors to force an explicit "reject non-essential cookies"
// choice per ePrivacy + GDPR Art. 6/7, and California visitors to expose the
// CCPA "Do Not Sell or Share My Personal Information" link. Detection is
// best-effort using the browser's declared timezone — cheap, no network hop,
// no third-party service required.
export function detectRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    // EEA + UK + Switzerland timezones.
    const euPrefixes = ["Europe/", "Atlantic/Azores", "Atlantic/Canary", "Atlantic/Faroe", "Atlantic/Madeira"];
    if (euPrefixes.some(p => tz.startsWith(p))) return "eu";
    // California heuristic — timezone alone can't uniquely id CA, so we widen
    // to "Los_Angeles" (PT) which covers CA + a few neighbouring states. The
    // legal duty (CCPA) triggers on "residents of California", but showing
    // the opt-out link to more people is harmless — worst case they click it
    // and we log a preference we would have logged anyway.
    if (tz === "America/Los_Angeles") return "ca";
    return "world";
  } catch {
    return "world";
  }
}

// ── Consent Modal ──────────────────────────────────────────────────────────
// Explicit-consent gate on sign-in: three checkboxes (ToS, Privacy,
// Marketing/optional). Only ToS + Privacy are required — marketing is opt-in.
// The result is persisted to localStorage so returning visitors don't get
// re-prompted; the backend still stamps `terms_accepted_at` on account
// creation for the audit trail.
const CONSENT_KEY = "pb.consent.v1";

export function hasSignedConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw);
    return !!(p && p.terms && p.privacy);
  } catch {
    return false;
  }
}

export function ConsentModal({ open, onOpenChange, onAccept }) {
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [region, setRegion] = useState("world");

  useEffect(() => { if (open) setRegion(detectRegion()); }, [open]);

  const canAccept = terms && privacy;

  const accept = () => {
    try {
      window.localStorage.setItem(CONSENT_KEY, JSON.stringify({
        terms: true, privacy: true, marketing,
        region, at: new Date().toISOString(),
      }));
    } catch {}
    onAccept?.({ marketing });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="consent-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400"/> Before you continue
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-400 -mt-2 mb-4">
          Photo Bridge processes personal data under the {region === "eu" ? "EU / UK GDPR" : region === "ca" ? "California Consumer Privacy Act" : "policies below"}.
          Please tell us what you agree to before we create your account.
        </p>

        <div className="space-y-3 text-sm">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e)=>setTerms(e.target.checked)}
              data-testid="consent-terms"
              className="mt-1 h-4 w-4 rounded border-white/20 bg-stone-900 accent-amber-500"
            />
            <span className="text-stone-300 leading-relaxed">
              I have read and agree to the <Link to="/legal/terms" className="text-amber-400 underline">Terms of Service</Link> and the <Link to="/legal/acceptable-use" className="text-amber-400 underline">Acceptable Use Policy</Link>. <span className="text-red-400">(required)</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={privacy}
              onChange={(e)=>setPrivacy(e.target.checked)}
              data-testid="consent-privacy"
              className="mt-1 h-4 w-4 rounded border-white/20 bg-stone-900 accent-amber-500"
            />
            <span className="text-stone-300 leading-relaxed">
              I have read the <Link to="/legal/privacy" className="text-amber-400 underline">Privacy Policy</Link> and agree to Photo Bridge processing my personal data as described (including transfers to the US under Standard Contractual Clauses). <span className="text-red-400">(required)</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e)=>setMarketing(e.target.checked)}
              data-testid="consent-marketing"
              className="mt-1 h-4 w-4 rounded border-white/20 bg-stone-900 accent-amber-500"
            />
            <span className="text-stone-300 leading-relaxed">
              I want to receive occasional product tips and feature announcements by email. <span className="text-stone-500">(optional — you can change this any time from Settings)</span>
            </span>
          </label>
        </div>

        {region === "ca" && (
          <p className="text-[11px] text-stone-500 border-t border-white/5 pt-3 mt-4">
            Californians: Photo Bridge does not "sell" or "share" personal information for cross-context behavioural advertising, but you retain the right to opt out at any time via <Link to="/legal/privacy" className="text-amber-400 underline">Privacy Policy → Do Not Sell or Share</Link>.
          </p>
        )}

        <div className="flex items-center justify-end gap-3 mt-5">
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="consent-cancel">Cancel</Button>
          <Button
            onClick={accept}
            disabled={!canAccept}
            data-testid="consent-accept"
            className="bg-amber-500 text-stone-950 hover:bg-amber-400"
          >Agree & continue</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── EU Cookie Banner ───────────────────────────────────────────────────────
// Only rendered for EEA / UK visitors. Two-button pattern (reject all / accept)
// is the minimum ePrivacy-compliant shape. Stored on the same
// `pb.cookies.v1` key so returning EU visitors don't get re-prompted.
const COOKIE_KEY = "pb.cookies.v1";

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [region, setRegion] = useState("world");

  useEffect(() => {
    const r = detectRegion();
    setRegion(r);
    if (r !== "eu") return;   // Only prompt EEA/UK.
    try {
      if (!window.localStorage.getItem(COOKIE_KEY)) setVisible(true);
    } catch { setVisible(true); }
  }, []);

  const choose = (allow) => {
    try { window.localStorage.setItem(COOKIE_KEY, JSON.stringify({ analytics: allow, at: new Date().toISOString() })); } catch {}
    setVisible(false);
  };

  if (!visible || region !== "eu") return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-banner"
      className="fixed bottom-3 left-3 right-3 md:left-6 md:right-auto md:max-w-md z-50 rounded-lg border border-white/10 bg-stone-950/95 backdrop-blur shadow-2xl"
    >
      <div className="p-4 flex gap-3">
        <Cookie className="w-5 h-5 text-amber-400 shrink-0 mt-0.5"/>
        <div className="flex-1 text-sm">
          <p className="text-stone-200 mb-1">We use cookies</p>
          <p className="text-xs text-stone-400 leading-relaxed">
            Strictly-necessary cookies keep you signed in. Analytics cookies help us improve — we ask for your consent before setting them, per the EU ePrivacy Directive. See <Link to="/legal/cookies" className="text-amber-400 underline">Cookie Policy</Link>.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={()=>choose(false)}
              data-testid="cookie-reject"
              className="px-3 py-1.5 text-xs rounded border border-white/10 text-stone-300 hover:text-white hover:bg-stone-800"
            >Reject non-essential</button>
            <button
              type="button"
              onClick={()=>choose(true)}
              data-testid="cookie-accept"
              className="px-3 py-1.5 text-xs rounded bg-amber-500 text-stone-950 hover:bg-amber-400 font-medium"
            >Accept all</button>
          </div>
        </div>
      </div>
    </div>
  );
}
